import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Modal } from "react-bootstrap";
import { sendResetPassword } from "@/api/LoginSignup";
import { checkDeletedUserEmail } from "@/api/myProfile";
import { checkEmail, emailNotFound, userDeletedEmail } from "@/setting";
import "./ResetPasswordModal.css";
import LoadingText from "../loader/LoadingText";
import LoadedText from "../loader/LoadedText";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";
import { useSelector } from "react-redux";
import { loginCmsDataSelector } from "@/redux/cms/cmsSelectors";

const ResetPasswordModal = ({
  show,
  onHide,
  userData,
  handleGoogleLogin,
  handleAppleLogin,
  customerCareNumber = "(855) 882-1314",
  termsLink = "#",
  privacyLink = "#",
  redirectionRequired,
}) => {
  const navigate = useNavigate("");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailSuccess, setEmailSucess] = useState(false);
  const [activeTab, setActiveTab] = useState("loginTab");
  const [isLoading, setIsLoading] = useState(false);
  const [showCustomerCare, setShowCustomerCare] = useState(true);
  const loginCmsData = useSelector(loginCmsDataSelector);

  const resetState = () => {
    onHide();
    setEmail("");
    setEmailError(false);
    setEmailSucess(false);
    setErrorMessage("");
    setErrorMessage("");
    setShowCustomerCare(true);
  };

  const handleClose = () => {
    resetState();

    trackButtonClick({
      eventName: "password_reset_modal_closed",
      eventCategory: "Authentication",
      eventSource: "Close Button",
      eventLabel: "Forgot Password Modal",
    });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setErrorMessage("");
    if (value.length === 0) {
      setEmailError(false);
      setErrorMessage("");
    } else {
      if (!emailRegex.test(value)) {
        setEmailError(true);
        setErrorMessage("Invalid Email Id");
      } else {
        setEmailError(false);
        setErrorMessage("");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailError) return;

    // Validate email
    if (!email || email.trim() === "") {
      setEmailError(true);
      setErrorMessage("Email address is required.");
      return;
    }

    trackButtonClick({
      eventName: "forgot_password_submit_click",
      eventCategory: "New-Registration",
      eventSource: "forgot password click",
    });

    setIsLoading(true);

    try {
      const sendReset = await sendResetPassword(email);

      if (sendReset.status === "SUCCESS") {
        setEmailSucess(true);
        setShowCustomerCare(false);

        trackApiCall({
          eventName: "forgot_password_success",
          eventCategory: "New-Registration",
          eventSource: "forgot password click",
        });
      } else {
        setEmailError(true);
        // Convert error codes to user-friendly messages
        const errorCode = sendReset.errorMessage;
        let userFriendlyMessage;

        switch (errorCode) {
          case "INVALID_USER":
            userFriendlyMessage = emailNotFound;
            break;
          case "SCREEN_NAME_NOT_ALLOWED":
            userFriendlyMessage =
              "This email address cannot be used for password reset.";
            break;
          case "ACCOUNT_LOCKED":
            userFriendlyMessage =
              "Your account has been locked. Please contact customer support.";
            break;
          case "RATE_LIMIT_EXCEEDED":
            userFriendlyMessage =
              "Too many password reset attempts. Please try again later.";
            break;
          case "EMAIL_DELIVERY_FAILED":
            userFriendlyMessage =
              "Unable to send email. Please verify your email address.";
            break;
          default:
            userFriendlyMessage =
              sendReset.errorMessage ||
              "Failed to send reset email. Please try again later.";
        }

        setErrorMessage(userFriendlyMessage);
      }
    } catch (error) {
      console.error("Password reset failed:", error);
      setEmailError(true);
      setErrorMessage("Failed to send reset email. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const checkIsEmailDeleted = async () => {
    if (email.length === 0 || emailError) return;
    try {
      const encodedEmail = encodeURIComponent(email);
      const deletedUserCheck = await checkDeletedUserEmail(
        `emailId=${encodedEmail}`
      );
      if (deletedUserCheck.data.data) {
        setEmailError(true);
        setErrorMessage(userDeletedEmail);
        setErrorMessage(emailNotFound);
      } else {
        setEmailError(false);
        setErrorMessage("");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRedirection = () => {
    handleClose();
    redirectionRequired && navigate("/");
  };

  useEffect(() => {
    if (show && userData.emailId) {
      setEmail(userData.emailId);
    } else if (show) {
      setEmail("");
    }
  }, [show, userData.emailId]);

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body className="p-4">
        <div className="login-pop container">
          <ul className="d-flex gap-5">
            <li
              className={`cursor-pointer disabled ${
                activeTab === "loginTab" ? "reset-modal-active-tab" : ""
              }`}
            >
              Sign In
            </li>
            <li className={`cursor-pointer`}>Register</li>
          </ul>
        </div>

        {showCustomerCare && (
          <p className="fs-15 welcome-para my-4 me-2">
            {loginCmsData?.resetPasswordText}
            <a
              href={`tel:${loginCmsData?.customerCareNumber}`}
              className="reset-customer-care"
            >
              {loginCmsData?.customerCareNumber}
            </a>
            .
          </p>
        )}

        <>
          {emailSuccess ? (
            <>
              <p className="fs-15 welcome-para">
                {checkEmail.replace("{email}", email)}
              </p>
              <button
                type="button"
                className="btn btn-log w-100 mt-2 reset-modal-submit-btn"
                onClick={handleRedirection}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <div
                className={`reset-input-container ${
                  emailError ? "input-error" : ""
                }`}
              >
                <input
                  type="text"
                  className="form-control reset-input-field mb-0"
                  placeholder="Email ID"
                  value={email}
                  onChange={handleChange}
                  onBlur={checkIsEmailDeleted}
                />
                <div className="reset-input-icon">
                  <i className="flaticon-user"></i>
                </div>
              </div>
              {emailError && (
                <div className="reset-modal-error">{errorMessage}</div>
              )}
              <button
                type="submit"
                className="btn btn-log w-100 mt-2 reset-modal-submit-btn"
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <LoadingText text="Sending..." />
                ) : (
                  <LoadedText text="Send" />
                )}
              </button>
            </>
          )}
        </>

        <div className="row mt25 socialLogins">
          <div className="col-lg-12">
            <p className="welcome-para">
              Or, sign in with your Google account:
            </p>
            <button
              type="button"
              className="btn btn-googl w-100 reset-social-btn"
              onClick={handleGoogleLogin}
            >
              <img
                src="/assets/images/googleIcon.png"
                alt="Google Icon"
                height={20}
                width={20}
                className="mx-3"
              />
              Continue with Google
            </button>
            <p className="welcome-para">Or, sign in with your Apple account:</p>
            <button
              type="button"
              className="btn btn-fb w-100 reset-social-btn"
              onClick={handleAppleLogin}
            >
              <i className="fa fa-apple fs-24"></i> Continue with Apple
            </button>

            <p className="welcome-para mt-3">
              By clicking on Continue with Google, or Continue with Apple, you
              accept the Hubzu
              <a
                href={termsLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: "5px", color: "#0d6efd" }}
              >
                Terms & Conditions
              </a>
              and
              <a
                href={privacyLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: "5px", color: "#0d6efd" }}
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ResetPasswordModal;
